import { createSlice } from '@reduxjs/toolkit';
import _ from 'lodash';
import {
  CruiseInitialValue, defaultStateroomFilling, FiltersInitialValue,
  PortOfDepartureOption, SearchModes, SearchStatus,
  HotelPageSettingsInitialValue,
  FlightPageSettingsInitialValue,
  CarPageSettingsInitialValue,
  FlightStopsFilterValues,
  TourPageSettingsInitialValue,
  HotelRatings,
  CustomInventoryPageSettingsInitialValue,
  allianceAutoComplete,
} from '../../constants';
import decodeLocationTypeObj from '../../utils/decodeUrl/decodeLocationTypeObj';
import getObjectDifferenceByDeepEqual from '../../utils/getObjectDifferenceByDeepEqual';
import normalizeSearchObjForQueryString from '../../utils/normalizeSearchObjForQueryString';
import generateUniqueId from '../../utils/generateUniqueId';

const parseQueryStateRoomsObj = (queryStaterooms) => (queryStaterooms
    && queryStaterooms.map((stateroom) => {
      const children = stateroom.children
        ? stateroom.children.map((child) => Number(child || 0)) : [];
      // const lapInfants = _.filter(children, (c) => c < passengerTypes.children.fromAge);
      // children = _.filter(children, (c) => c >= passengerTypes.children.fromAge);
      return {
        ...stateroom,
        adults: Number(stateroom.adults || 0),
        seniors: Number(stateroom.seniors || 0),
        lapInfants: Number(stateroom.lapInfants || 0),
        seatInfants: Number(stateroom.seatInfants || 0),
        children,
        discounts: stateroom.discounts
          ? stateroom.discounts.map((discount) => Number(discount))
          : [],
        passengerOccupation: stateroom.passengerOccupation || '',
        pastPassengerNumbers: stateroom.pastPassengerNumbers || [],
      };
    }));

const initialState = {
  /// searched hotel trip search inputs
  searchObj: {
    staterooms: [defaultStateroomFilling],
    hotel: {
      doShowOnlyAgentPreferredHotels: false,
      doShowAvailableHotelsOnly: false,
      checkInDate: undefined,
      checkOutDate: undefined,
      selectedAddress: undefined,
      filterRating: undefined,
      origin: null,
      rateCodes: undefined,
      exactMatchRateCodesOnly: undefined,
      doShowCommissionableRatesOnly: false,
      showRefundableHotelRatesOnly: false,
      ...HotelPageSettingsInitialValue,
      searchRadiuses: undefined,
    },
    cruise: FiltersInitialValue.cruise,
    flight: {
      ...FiltersInitialValue.flight,
      ...FlightPageSettingsInitialValue,
    },
    car: {
      ...FiltersInitialValue.car,
      ...CarPageSettingsInitialValue,
      responseFilters: {},
    },
    tour: {
      destinations: [],
      startDate: undefined,
      endDate: undefined,
      origin: undefined,
      ...TourPageSettingsInitialValue,
    },
    customInventory: {
      destinations: [],
      startDate: undefined,
      endDate: undefined,
      origin: undefined,
      ...CustomInventoryPageSettingsInitialValue,
    },
  },
  /// hotel trip search inputs in edit
  searchInputs: {
    staterooms: [defaultStateroomFilling],
    hotel: {
      doShowOnlyAgentPreferredHotels: undefined,
      doShowAvailableHotelsOnly: undefined,
      checkInDate: undefined,
      checkOutDate: undefined,
      selectedAddress: undefined,
      filterRating: undefined,
      origin: null,
      rateCodes: undefined,
      exactMatchRateCodesOnly: undefined,
      doShowCommissionableRatesOnly: false,
      showRefundableHotelRatesOnly: false,
      searchRadiuses: undefined,
      ...HotelPageSettingsInitialValue,
    },
    cruise: CruiseInitialValue.searchFilter,
    flight: {
      ...FiltersInitialValue.flight,
      ...FlightPageSettingsInitialValue,
    },
    car: {
      ...FiltersInitialValue.car,
      ...CarPageSettingsInitialValue,
      responseFilters: {},
    },
    tour: {
      destinations: [],
      startDate: undefined,
      endDate: undefined,
      origin: undefined,
      ...TourPageSettingsInitialValue,
    },
    customInventory: {
      destinations: [],
      startDate: undefined,
      endDate: undefined,
      origin: undefined,
      ...CustomInventoryPageSettingsInitialValue,
    },
  },
  searchResult: {
    travelOptions: [],
    cachedItinerariesChanges: [],
  },
  selectedSearchMode: SearchModes.CRUISE_TRIP,
  searchStatus: SearchStatus.INITIAL,
  isMapShown: true,
  currencyCode: 'USD',
  stateroomsByMode: {},
};

const tripSearchSlice = createSlice({
  name: 'tripSearch',
  initialState,
  reducers: {
    // When user not logged in, don't reset search status to initial,
    // otherwise change search mode will not work in results page
    resetSearchResults: (state, action) => ({
      ...state,
      searchStatus: action && action.payload && action.payload.isLoggedIn
        ? initialState.searchStatus : state.searchStatus,
      searchResult: initialState.searchResult,
      isResetSearchResult: true,
    }),
    clearSearch: () => (state) => ({
      ...state,
      searchObj: { ...initialState.searchObj },
      searchInputs: { ...initialState.searchInputs },
    }),
    setCruiseSearchParam: (state, { payload }) => {
      const nextSearchInputs = { ...payload };
      const staterooms = payload.staterooms || state.searchObj.staterooms;
      delete nextSearchInputs.staterooms;
      if (nextSearchInputs.filters) delete nextSearchInputs.filters.staterooms;

      return {
        ...state,
        searchObj: {
          ...state.searchObj,
          staterooms,
          cruise: {
            ...state.searchObj.cruise,
            ...nextSearchInputs,
          },
        },
      };
    },
    setCruiseSearchObjFromQueryString: (state, { payload }) => {
      const {
        duration: queryStringDuration,
        locations: queryStringLocations,
        fromDate: queryStringFromDate,
        toDate: queryStringToDate,
        filters: queryStringFilters,
        page: queryStringPage,
        sortBy: queryStringSortBy,
        sortDirection: queryStringSortDirection,
        staterooms: queryStringStaterooms,
      } = payload;

      const nextSearchObj = {
        ...state.searchObj.cruise,
        filters: {
          ...state.searchObj.cruise.filters,
        },
      };

      if (queryStringDuration) {
        _.set(nextSearchObj, 'duration', {
          label: queryStringDuration.label,
          value: Number(queryStringDuration.value),
          from: queryStringDuration.from ? Number(queryStringDuration.from) : null,
          to: queryStringDuration.to ? Number(queryStringDuration.to) : null,
        });
      }
      if (queryStringFromDate) {
        _.set(nextSearchObj, 'fromDate', queryStringFromDate);
      }
      if (queryStringToDate) {
        _.set(nextSearchObj, 'toDate', queryStringToDate);
      }
      if (queryStringPage) {
        _.set(nextSearchObj, 'page', Number(queryStringPage));
      }
      if (queryStringSortBy) {
        _.set(nextSearchObj, 'sortBy', Number(queryStringSortBy));
      }
      if (queryStringSortDirection) {
        _.set(nextSearchObj, 'sortDirection', Number(queryStringSortDirection));
      }
      if (queryStringLocations) {
        _.set(nextSearchObj, 'locations', queryStringLocations);
      }

      if (queryStringFilters) {
        if (queryStringFilters.origin) {
          const location = queryStringFilters.origin.latitude
            && queryStringFilters.origin.longitude ? {
              latitude: Number(queryStringFilters.origin.latitude),
              longitude: Number(queryStringFilters.origin.longitude),
            } : {};
          _.set(nextSearchObj, 'filters.origin', {
            ...queryStringFilters.origin,
            ...location,
          });
        }
        if (queryStringFilters.city) {
          _.set(nextSearchObj, 'filters.city', queryStringFilters.city);
        }
        if (queryStringFilters.cruiseLines) {
          _.set(nextSearchObj, 'filters.cruiseLines', queryStringFilters.cruiseLines);
        }
        if (queryStringFilters.cruiseShips) {
          _.set(nextSearchObj, 'filters.cruiseShips', queryStringFilters.cruiseShips);
        }

        if (queryStringFilters.discounts && queryStringFilters.discounts.length) {
          _.set(nextSearchObj, 'filters.discounts', _.filter(_.map(queryStringFilters.discounts, (d) => (+d >= 0 ? +d : -1)), (d) => d !== -1));
        }

        _.set(nextSearchObj, 'filters.cityIds', queryStringFilters.cityIds || []);

        _.set(
          nextSearchObj,
          'filters.portOfDepartureOption',
          queryStringFilters.portOfDepartureOption
            ? Number(queryStringFilters.portOfDepartureOption) : PortOfDepartureOption.ANY,
        );
        if (queryStringFilters.cabinType) {
          _.set(nextSearchObj, 'filters.cabinType', Number(queryStringFilters.cabinType));
        }
      }

      let staterooms = null;
      if (queryStringStaterooms) {
        staterooms = parseQueryStateRoomsObj(queryStringStaterooms);
      }

      return {
        ...state,
        searchObj: {
          ...state.searchObj,
          staterooms: staterooms || state.searchObj.staterooms,
          cruise: nextSearchObj,
        },
        searchInputs: {
          ...state.searchInputs,
          staterooms: staterooms || state.searchInputs.staterooms,
          cruise: {
            ...state.searchInputs.cruise,
            ..._.omit(nextSearchObj, ['page', 'sortBy', 'sortDirection', 'pageSize']),
            areFiltersEmpty: _.isEqual(
              nextSearchObj.filters,
              CruiseInitialValue.searchFilter.filters,
            ),
          },
        },
      };
    },
    setHotelSearchObjFromQueryString: (state, { payload }) => {
      const {
        staterooms: queryStrStaterooms,
        doShowOnlyAgentPreferredHotels: queryStrDoShowOnlyAgentPreferredHotels,
        doShowCommissionableRatesOnly: queryShowCommissionableRatesOnly,
        showRefundableHotelRatesOnly: queryShowRefundableHotelRatesOnly,
        showUnavailableHotels: queryShowUnavailableHotels,
        checkInDate: queryStrCheckInDate,
        checkOutDate: queryStrCheckOutDate,
        filterRating: queryStrFilterRating,
        selectedAddress: queryStrSelectedAddress,
        origin: queryStrOrigin,
        rateCodes: queryRateCodes,
        hproviders: queryHotelProvidersToInclude,
        exactMatchRateCodesOnly: queryExactMatchRateCodesOnly,
        hotelChains: queryHotelChains,
        hotelNames: queryHotelNames,
        fp: fromPrice,
        tp: toPrice,
        searchRadiuses: querySearchRadiuses,
      } = payload;

      const nextSearchObj = {
        ...state.searchObj.hotel,
      };

      if (queryStrCheckInDate) {
        _.set(nextSearchObj, 'checkInDate', queryStrCheckInDate);
      }
      if (queryStrCheckOutDate) {
        _.set(nextSearchObj, 'checkOutDate', queryStrCheckOutDate);
      }
      if (queryStrDoShowOnlyAgentPreferredHotels) {
        _.set(nextSearchObj, 'doShowOnlyAgentPreferredHotels', queryStrDoShowOnlyAgentPreferredHotels === 'true');
      }
      if (queryShowCommissionableRatesOnly) {
        _.set(nextSearchObj, 'doShowCommissionableRatesOnly', queryShowCommissionableRatesOnly === 'true');
      }
      if (queryShowRefundableHotelRatesOnly) {
        _.set(nextSearchObj, 'showRefundableHotelRatesOnly', queryShowRefundableHotelRatesOnly === 'true');
      }
      _.set(nextSearchObj, 'doShowAvailableHotelsOnly', !queryShowUnavailableHotels || queryShowUnavailableHotels !== 'true');
      if (queryStrFilterRating) {
        _.set(nextSearchObj, 'filterRating', +queryStrFilterRating);
      } else {
        _.set(nextSearchObj, 'filterRating', HotelRatings.ANY);
      }
      if (queryStrSelectedAddress) {
        _.set(nextSearchObj, 'selectedAddress', queryStrSelectedAddress);
      }

      if (queryRateCodes && queryRateCodes.length) _.set(nextSearchObj, 'rateCodes', queryRateCodes || []);
      else _.set(nextSearchObj, 'rateCodes', queryRateCodes && Object.values(queryRateCodes).length ? Object.values(queryRateCodes) : []);
      if (queryHotelProvidersToInclude && queryHotelProvidersToInclude.length) _.set(nextSearchObj, 'hotelProvidersToInclude', queryHotelProvidersToInclude || []);
      else _.set(nextSearchObj, 'hotelProvidersToInclude', []);

      if (queryHotelChains && queryHotelChains.length > 0) {
        _.set(nextSearchObj, 'filterHotelChains', queryHotelChains);
      }

      if (queryHotelNames && queryHotelNames.length > 0) {
        _.set(nextSearchObj, 'filterHotelKeys', queryHotelNames.map((n) => ({ value: atob(n.value), label: n.label })));
      }

      if (queryExactMatchRateCodesOnly) {
        _.set(nextSearchObj, 'exactMatchRateCodesOnly', queryExactMatchRateCodesOnly === 'true');
      }

      if (queryStrOrigin) {
        _.set(nextSearchObj, 'origin', queryStrOrigin);
      }

      if (toPrice) {
        _.set(nextSearchObj, 'toPrice', +toPrice);
      }

      if (fromPrice) {
        _.set(nextSearchObj, 'fromPrice', +fromPrice);
      }

      if (querySearchRadiuses && querySearchRadiuses.length > 0) {
        _.set(nextSearchObj, 'searchRadiuses', querySearchRadiuses.map((r) => +r));
      }

      let staterooms = null;
      if (queryStrStaterooms) {
        staterooms = parseQueryStateRoomsObj(queryStrStaterooms);
      }

      return {
        ...state,
        searchObj: {
          ...state.searchObj,
          staterooms: staterooms || state.searchObj.staterooms,
          hotel: nextSearchObj,
        },
        searchInputs: {
          ...state.searchInputs,
          staterooms: staterooms || state.searchInputs.staterooms,
          hotel: {
            ...state.searchInputs.hotel,
            ...nextSearchObj,
          },
        },
      };
    },
    setFlightSearchObjFromQueryString: (state, { payload }) => {
      const {
        maxNumOfStops,
        avoidCabinDowngrade,
        keepSameCabin,
        flightSegments: queryFlightSegments,
        flightSearchType: queryFlightSearchType,
        staterooms: queryStrStaterooms,
        airlines: qsAirlines,
        alliances: qsAlliances,
        rateCodes: qsRateCodes,
        exactMatchRateCodesOnly: queryExactMatchRateCodesOnly,
        diffCabins: qsDiffCabins,
        diffAirlines: qsdiffAirlines,
        diffAlliances: qsdiffAlliances,
        diffConnectingAirports: qsdiffConnectingAirports,
        doShowRefundableFlightsOnly: queryDoShowRefundableFlightsOnly,
        doShowLongConnectFlights: queryDoShowLongConnectFlights,
        hideNDC: queryDoHideNdcFares,
        doEnableFlightsPolling: queryDoEnableFlightsPolling,
        airlinesPreference: qsAirlinesPreference,
        alliancePreference: qsAlliancePreference,
      } = payload;

      const nextSearchObj = {
        ...state.searchObj.flight,
      };

      if (maxNumOfStops !== undefined && +maxNumOfStops >= 0) {
        _.set(nextSearchObj, 'maxNumOfStops', +maxNumOfStops);
      } else {
        _.set(nextSearchObj, 'maxNumOfStops', FlightStopsFilterValues.ANY.value);
      }

      if (avoidCabinDowngrade !== undefined) {
        _.set(nextSearchObj, 'avoidCabinDowngrade', avoidCabinDowngrade === 'true');
      }

      if (keepSameCabin !== undefined) {
        _.set(nextSearchObj, 'keepSameCabin', keepSameCabin === 'true');
      }

      if (qsAirlinesPreference !== undefined && +qsAirlinesPreference >= 0) {
        _.set(nextSearchObj, 'airlinesPreference', +qsAirlinesPreference);
      }
      if (qsAlliancePreference !== undefined && +qsAlliancePreference >= 0) {
        _.set(nextSearchObj, 'alliancePreference', +qsAlliancePreference);
      }
      if (queryFlightSegments) {
        if (queryFlightSegments.length > 0) {
          for (let i = 0; i < queryFlightSegments.length; i += 1) {
            if (queryFlightSegments[i].airlinesPreference !== undefined
              && +queryFlightSegments[i].airlinesPreference >= 0
            ) {
              queryFlightSegments[i].airlinesPreference = +queryFlightSegments[i]
                .airlinesPreference;
            }
          }
        }
        if (queryFlightSegments.length > 0) {
          for (let i = 0; i < queryFlightSegments.length; i += 1) {
            if (queryFlightSegments[i].connectingAirportsPreference !== undefined
              && +queryFlightSegments[i].connectingAirportsPreference >= 0
            ) {
              queryFlightSegments[i].connectingAirportsPreference = +queryFlightSegments[i]
                .connectingAirportsPreference;
            }
          }
        }
        if (queryFlightSegments.length > 0) {
          for (let i = 0; i < queryFlightSegments.length; i += 1) {
            if (queryFlightSegments[i].alliancePreference !== undefined
              && +queryFlightSegments[i].alliancePreference >= 0
            ) {
              queryFlightSegments[i].alliancePreference = +queryFlightSegments[i]
                .alliancePreference;
            }
            if (queryFlightSegments[i].alliances && queryFlightSegments[i].alliances.length > 0) {
              const alliances = queryFlightSegments[i].alliances.split(',');
              queryFlightSegments[i].alliances = _.filter(allianceAutoComplete,
                (a) => alliances.includes(a.value));
            }
          }
        }

        _.set(nextSearchObj, 'flightSegments', queryFlightSegments);
        _.set(nextSearchObj, 'flightSearchType', queryFlightSearchType);
      }
      if (qsAirlines) {
        _.set(nextSearchObj, 'airlines', qsAirlines);
      }
      if (qsAlliances && qsAlliances.length > 0) {
        _.set(nextSearchObj, 'alliances', _.filter(allianceAutoComplete, (a) => qsAlliances.split(',').includes(a.value)));
      }

      _.set(nextSearchObj, 'hasDifferentOutboundInboundCabins', qsDiffCabins || false);

      _.set(nextSearchObj, 'hasDifferentOutboundInboundAirLine', qsdiffAirlines || false);
      _.set(nextSearchObj, 'hasDifferentOutboundInboundConnectingAirports', qsdiffConnectingAirports || false);
      _.set(nextSearchObj, 'hasDifferentOutboundInboundAlliance', qsdiffAlliances || false);
      _.set(nextSearchObj, 'rateCodes', qsRateCodes || []);

      _.set(nextSearchObj, 'exactMatchRateCodesOnly', queryExactMatchRateCodesOnly === 'true');

      if (queryDoShowRefundableFlightsOnly) {
        _.set(nextSearchObj, 'doShowRefundableFlightsOnly', queryDoShowRefundableFlightsOnly === 'true');
      }
      if (queryDoShowLongConnectFlights) {
        _.set(nextSearchObj, 'doShowLongConnectFlights', queryDoShowLongConnectFlights === 'true');
      }
      if (queryDoHideNdcFares !== undefined) {
        _.set(nextSearchObj, 'doHideNdcFares', queryDoHideNdcFares === 'true');
      }
      if (queryDoEnableFlightsPolling) {
        _.set(nextSearchObj, 'doEnableFlightsPolling', queryDoEnableFlightsPolling === 'true');
      }
      let staterooms = null;
      if (queryStrStaterooms) {
        staterooms = parseQueryStateRoomsObj(queryStrStaterooms);
      }

      return {
        ...state,
        searchObj: {
          ...state.searchObj,
          staterooms: staterooms || state.searchObj.staterooms,
          flight: nextSearchObj,
        },
        searchInputs: {
          ...state.searchInputs,
          staterooms: staterooms || state.searchInputs.staterooms,
          flight: {
            ...state.searchInputs.flight,
            ...nextSearchObj,
          },
        },
      };
    },
    setCarSearchObjFromQueryString: (state, { payload }) => {
      const {
        staterooms: queryStrStaterooms,
        carSearchType,
        pickupCode,
        dropoffCode,
        pickupDateTime,
        returnDateTime,
        carTypes,
        transmissionTypes,
        rentalCarAgencies,
        fromBudget,
        toBudget,
        origin,
        rateCodes,
      } = payload;

      const nextSearchObj = {
        ...state.searchObj.car,
      };

      if (carSearchType !== undefined) {
        _.set(nextSearchObj, 'carSearchType', +carSearchType);
      }
      if (pickupCode) {
        _.set(nextSearchObj, 'pickupCode', decodeLocationTypeObj(pickupCode));
      }
      if (dropoffCode) {
        _.set(nextSearchObj, 'dropoffCode', decodeLocationTypeObj(dropoffCode));
      }
      if (pickupDateTime) {
        _.set(nextSearchObj, 'pickupDateTime', pickupDateTime);
      }
      if (returnDateTime) {
        _.set(nextSearchObj, 'returnDateTime', returnDateTime);
      }
      if (carTypes) {
        _.set(nextSearchObj, 'carTypes', carTypes);
      }
      if (transmissionTypes) {
        _.set(nextSearchObj, 'transmissionTypes', transmissionTypes || []);
      }
      if (rentalCarAgencies) {
        _.set(nextSearchObj, 'rentalCarAgencies', rentalCarAgencies);
      }
      if (fromBudget) {
        _.set(nextSearchObj, 'fromBudget', fromBudget);
      }
      if (toBudget) {
        _.set(nextSearchObj, 'toBudget', toBudget);
      }
      if (origin) {
        _.set(nextSearchObj, 'origin', origin);
      }
      _.set(nextSearchObj, 'rateCodes', rateCodes || []);

      let staterooms = null;
      if (queryStrStaterooms) {
        staterooms = parseQueryStateRoomsObj(queryStrStaterooms);
      }

      return {
        ...state,
        searchObj: {
          ...state.searchObj,
          staterooms: staterooms || state.searchObj.staterooms,
          car: nextSearchObj,
        },
        searchInputs: {
          ...state.searchInputs,
          staterooms: staterooms || state.searchInputs.staterooms,
          car: {
            ...state.searchInputs.car,
            ...nextSearchObj,
          },
        },
      };
    },
    setCustomInventorySearchObjFromQueryString: (state, { payload }) => {
      const {
        staterooms: queryStrStaterooms,
        startDate: queryStrstartDate,
        endDate: queryStrEndDate,
        origin: queryStrOrigin,
        destinations: queryDestinations,
      } = payload;

      const key = 'customInventory';
      const nextSearchObj = {
        ...state.searchObj[key],
      };

      if (queryStrstartDate) {
        _.set(nextSearchObj, 'startDate', queryStrstartDate);
      }
      if (queryStrEndDate) {
        _.set(nextSearchObj, 'endDate', queryStrEndDate);
      }
      if (queryStrOrigin) {
        _.set(nextSearchObj, 'origin', queryStrOrigin);
      }
      if (queryDestinations) {
        _.set(nextSearchObj, 'destinations', queryDestinations);
      }

      let staterooms = null;
      if (queryStrStaterooms) {
        staterooms = parseQueryStateRoomsObj(queryStrStaterooms);
      }

      return {
        ...state,
        searchObj: {
          ...state.searchObj,
          staterooms: staterooms || state.searchObj.staterooms,
          [key]: nextSearchObj,
        },
        searchInputs: {
          ...state.searchInputs,
          staterooms: staterooms || state.searchInputs.staterooms,
          [key]: {
            ...state.searchInputs[key],
            ...nextSearchObj,
          },
        },
      };
    },
    setTourSearchObjFromQueryString: (state, { payload }) => {
      const {
        staterooms: queryStrStaterooms,
        startDate: queryStrstartDate,
        endDate: queryStrEndDate,
        origin: queryStrOrigin,
        destinations: queryDestinations,
      } = payload;
      const key = 'tour';
      const nextSearchObj = {
        ...state.searchObj[key],
      };

      if (queryStrstartDate) {
        _.set(nextSearchObj, 'startDate', queryStrstartDate);
      }
      if (queryStrEndDate) {
        _.set(nextSearchObj, 'endDate', queryStrEndDate);
      }
      if (queryStrOrigin) {
        _.set(nextSearchObj, 'origin', queryStrOrigin);
      }
      if (queryDestinations) {
        _.set(nextSearchObj, 'destinations', queryDestinations);
      }

      let staterooms = null;
      if (queryStrStaterooms) {
        staterooms = parseQueryStateRoomsObj(queryStrStaterooms);
      }

      return {
        ...state,
        searchObj: {
          ...state.searchObj,
          staterooms: staterooms || state.searchObj.staterooms,
          [key]: nextSearchObj,
        },
        searchInputs: {
          ...state.searchInputs,
          staterooms: staterooms || state.searchInputs.staterooms,
          [key]: {
            ...state.searchInputs[key],
            ...nextSearchObj,
          },
        },
      };
    },
    setCruiseSearchInputs: (state, { payload }) => {
      const nextSearchFilter = {
        ...state.searchInputs.cruise,
        ...payload,
        filters: {
          ...state.searchInputs.cruise.filters,
          ...payload.filters,
        },
      };
      const areFiltersEmpty = _.isEqual(
        nextSearchFilter.filters,
        CruiseInitialValue.searchFilter.filters,
      );
      const staterooms = payload.staterooms || state.searchInputs.staterooms;
      delete nextSearchFilter.staterooms;
      delete nextSearchFilter.filters.staterooms;

      return {
        ...state,
        // searchObj: {
        //   ...state.searchObj,
        //   staterooms,
        //   cruise: nextSearchFilter,
        // },
        searchInputs: {
          ...state.searchInputs,
          staterooms,
          cruise: {
            ...state.searchInputs.cruise,
            ...nextSearchFilter,
            ...{ areFiltersEmpty },
          },
        },
      };
    },
    clearCruiseSearchFilters: (state) => ({
      ...state,
      searchInputs: {
        ...state.searchInputs,
        staterooms: [defaultStateroomFilling],
        cruise: {
          ...state.searchInputs.cruise,
          ...{ filters: CruiseInitialValue.searchFilter.filters },
          ...{ areFiltersEmpty: true },
        },
      },
    }),
    setHotelTripSearchInputs: (state, { payload }) => ({
      ...state,
      searchInputs: {
        ...state.searchInputs,
        hotel: {
          ...state.searchInputs.hotel,
          ...payload,
        },
      },
    }),
    setHotelTripSearchParam: (state, { payload }) => {
      const inputPayload = { ...payload };
      const staterooms = inputPayload.staterooms || state.searchInputs.staterooms;
      delete inputPayload.staterooms;
      delete inputPayload.doIgnoreStoredResults;
      delete inputPayload.doGetRemainingHotelsOnly;

      return {
        ...state,
        searchObj: {
          ...state.searchObj,
          staterooms,
          hotel: {
            ...state.searchObj.hotel,
            ...inputPayload,
          },
        },
      };
    },

    setHotelRateDisplayPreference: (state, { payload }) => ({
      ...state,
      hotelPriceDisplayPreference: payload,
    }),

    setFlightTripSearchInputs: (state, { payload }) => ({
      ...state,
      searchInputs: {
        ...state.searchInputs,
        flight: {
          ...state.searchInputs.flight,
          ...payload,
        },
      },
    }),
    setFlightTripSearchParam: (state, { payload }) => {
      const inputPayload = { ...payload, ...(payload.searchObj || {}) };
      const staterooms = inputPayload.staterooms || state.searchObj.staterooms;
      delete inputPayload.staterooms;
      delete inputPayload.isTripSearch;
      delete inputPayload.searchObj;
      delete inputPayload.flightItinerary;
      delete inputPayload.searchStatus;

      return {
        ...state,
        searchStatus: payload.searchStatus !== null && payload.searchStatus !== undefined
          ? payload.searchStatus : state.searchStatus,
        searchObj: {
          ...state.searchObj,
          staterooms,
          flight: {
            ...state.searchObj.flight,
            ...inputPayload,
          },
        },
      };
    },

    setCarTripSearchInputs: (state, { payload }) => ({
      ...state,
      searchInputs: {
        ...state.searchInputs,
        car: {
          ...state.searchInputs.car,
          ...payload,
        },
      },
    }),

    setCarTripSearchParam: (state, { payload }) => {
      const inputPayload = { ...payload };
      const staterooms = inputPayload.staterooms || state.searchInputs.staterooms;
      delete inputPayload.staterooms;

      return {
        ...state,
        searchObj: {
          ...state.searchObj,
          staterooms,
          car: {
            ...state.searchObj.car,
            ...inputPayload,
          },
        },
      };
    },
    setTripSearchProperty: (state, { payload }) => ({
      ...state,
      ...payload,
    }),
    setTripSearchInputs: (state, { payload }) => ({
      ...state,
      searchInputs: {
        ...state.searchInputs,
        ...payload,
      },
    }),
    setTripSearchObj: (state, { payload }) => ({
      ...state,
      searchObj: {
        ...state.searchObj,
        ...payload,
      },
    }),

    cruiseSearchRequest: (state) => ({
      ...state,
      searchStatus: SearchStatus.SEARCHING,
    }),
    hotelSearchRequest: (state, action) => ({
      ...state,
      searchStatus: action && action.payload
      && (action.payload.doIgnoreStoredResults || action.payload.doGetRemainingHotelsOnly)
        ? state.searchStatus
        : SearchStatus.SEARCHING,
    }),
    initHotelSearchRequest: (state) => ({
      ...state,
      requestId: generateUniqueId(),
      searchStatus: SearchStatus.SEARCHING,
    }),
    pollingHotelSearchRequest: (state) => ({
      ...state,
    }),
    customInventorySearchRequest: (state) => ({
      ...state,
      searchStatus: SearchStatus.SEARCHING,
    }),
    tourSearchRequest: (state) => ({
      ...state,
      // searchStatus: SearchStatus.SEARCHING,
    }),

    setTourTripSearchParam: (state, { payload }) => {
      const inputPayload = { ...payload, ...(payload.searchObj || {}) };
      return {
        ...state,
        searchStatus: payload.searchStatus !== null && payload.searchStatus !== undefined
          ? payload.searchStatus : state.searchStatus,
        searchObj: {
          ...state.searchObj,
          tour: {
            ...state.searchObj.tour,
            ...inputPayload,
          },
        },
      };
    },

    setCustomInventoryTripSearchParam: (state, { payload }) => {
      const inputPayload = { ...payload, ...(payload.searchObj || {}) };
      return {
        ...state,
        searchStatus: payload.searchStatus !== null && payload.searchStatus !== undefined
          ? payload.searchStatus : state.searchStatus,
        searchObj: {
          ...state.searchObj,
          customInventory: {
            ...state.searchObj.customInventory,
            ...inputPayload,
          },
        },
      };
    },

    setFoundTotalHotels: (state, action) => ({
      ...state,
      searchResult: {
        ...state.searchResult,
        foundTotalHotels: action && action.payload,
      },
    }),
    setFilters: (state, { payload: { filters } }) => ({
      ...state,
      searchResult: {
        ...state.searchResult,
        filters: filters || state.searchResult.filters,
      },
    }),
    searchSuccess: (state, { payload }) => ({
      ...state,
      searchResult: {
        ...payload,
        foundTotalHotels: state.searchResult.foundTotalHotels,
      },
      searchStatus: SearchStatus.DONE,
    }),
    searchFail: (state, action) => ({
      ...state,
      error: action.payload,
      searchStatus: SearchStatus.DONE,
    }),
    setSelectedSearchMode: (state, { payload }) => ({
      ...state,
      selectedSearchMode: payload,
    }),
    toggleMapVisibility: (state, action) => ({
      ...state,
      isMapShown: typeof (action && action.payload) === 'boolean' ? action.payload : !state.isMapShown,
    }),
  },
});

export const getDirtySearchObject = ({ searchObj, searchedMode }) => {
  const searchObject = normalizeSearchObjForQueryString({ searchObj, searchedMode });
  const initialSearchObject = normalizeSearchObjForQueryString({
    searchObj: {
      ...initialState.searchObj,
      flight: {
        ...initialState.searchObj.flight,
        ...(searchObj.flight.defaultSearchObj || {}),
      },
      car: {
        ...initialState.searchObj.car,
        ...(searchObj.car.defaultSearchObj || {}),
      },
      tour: {
        ...initialState.searchObj.tour,
        ...(searchObj.tour.defaultSearchObj || {}),
      },
      customInventory: {
        ...initialState.searchObj.customInventory,
        ...(searchObj.customInventory.defaultSearchObj || {}),
      },
    },
    searchedMode,
  });
  return getObjectDifferenceByDeepEqual(searchObject, initialSearchObject);
};

export const {
  resetSearchResults, clearSearch, setCruiseSearchParam, setCruiseSearchObjFromQueryString,
  setHotelSearchObjFromQueryString, setCruiseSearchInputs, clearCruiseSearchFilters,
  setFlightSearchObjFromQueryString, setCarSearchObjFromQueryString,
  setCustomInventorySearchObjFromQueryString,
  setTourSearchObjFromQueryString,
  setHotelTripSearchInputs, setHotelTripSearchParam, setHotelRateDisplayPreference,
  setFlightTripSearchInputs, setFlightTripSearchParam, setFoundTotalHotels, setFilters,
  cruiseSearchRequest, searchSuccess, searchFail, hotelSearchRequest, initHotelSearchRequest,
  pollingHotelSearchRequest,
  customInventorySearchRequest,
  tourSearchRequest,
  setTripSearchInputs, setTripSearchObj,
  setSelectedSearchMode,
  setCarTripSearchInputs, setCarTripSearchParam, setTripSearchProperty,
  toggleMapVisibility, setTourTripSearchParam, setCustomInventoryTripSearchParam,
} = tripSearchSlice.actions;
export default tripSearchSlice.reducer;
